import { useAiOff } from '../api/consents'
import { useAnalysisAutoHeal, useProfile } from '../api/hooks'
import { ageFromDob } from '../lib/format'
import { HeroBanner } from '../components/dashboard/HeroBanner'
import { RangeCounters } from '../components/dashboard/RangeCounters'
import { ScanStrip } from '../components/dashboard/ScanStrip'
import { ReferenceTable } from '../components/dashboard/ReferenceTable'
import { BodyMapPanel } from '../components/dashboard/BodyMapPanel'
import { SessionDigest } from '../components/dashboard/SessionDigest'
import { TonnageChart } from '../components/dashboard/TonnageChart'
import { WeeklyAttendance } from '../components/dashboard/WeeklyAttendance'
import { AnalysisSections } from '../components/dashboard/AnalysisSections'
import {
  useAnalysisPanel,
  useAttendancePanel,
  useBodyMapPanel,
  useDigestPanel,
  useHeroPanel,
  useReferencePanel,
  useScanPanel,
  useRangeCountersPanel,
  useVolumePanel,
} from '../components/dashboard/panelStates'
import { PageError, SectionError } from '../components/errors/PanelError'
import { AsyncPanel, PanelSkeleton, Shimmer } from '../components/ui/AsyncPanel'
import { Hairline, Panel, Section } from '../components/ui/dashboard-primitives'
import { failsAsOne } from '../lib/queryState'

export default function Dashboard() {
  // Without AI processing the page shows one note where the analysis would be and asks the server for no
  // analysis (#399, CS-D14).
  const aiOff = useAiOff()
  useAnalysisAutoHeal(!aiOff)
  const { data: profile } = useProfile()

  // Every panel's state, read from the same hooks the panels render from, so a
  // shared failure is said once (the A+ ruling on #169): one failed panel shows
  // its own card, a section whose panels all failed shows one, and a page whose
  // sections all failed shows one. TanStack dedupes the repeated query calls.
  const hero = useHeroPanel()
  const counters = useRangeCountersPanel()
  const volume = useVolumePanel()
  const scan = useScanPanel()
  const reference = useReferencePanel()
  const bodyMap = useBodyMapPanel()
  const digest = useDigestPanel()
  const attendance = useAttendancePanel()
  const analysis = useAnalysisPanel(!aiOff)

  const strengthFailed = failsAsOne([reference, bodyMap])
  const loggedFailed = failsAsOne([digest, volume, attendance])
  // The analysis is not a panel of the page while the AI consent is missing: its note cannot fail.
  const allPanels = [hero, counters, volume, scan, reference, bodyMap, digest, attendance, ...(aiOff ? [] : [analysis])]
  const pageFailed = failsAsOne(allPanels)

  // useProfile() has not resolved on first paint, so this must read as a
  // complete sentence with no age in it too — never "Against a
  // undefined-year-old" while the query is in flight.
  const viewerAge = profile?.date_of_birth ? ageFromDob(profile.date_of_birth) : null
  const referenceTitle = viewerAge !== null ? `Against a ${viewerAge}-year-old` : 'Against your reference age'

  if (pageFailed) {
    return (
      <>
        <h1 className="sr-only">Dashboard</h1>
        <PageError subject="Your dashboard" sources={allPanels.flatMap((p) => p.sources)} />
      </>
    )
  }

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pb-24">
      {/* No visible page title by design — the hero opens with a greeting, not
          a label. It lives here rather than in HeroBanner because HeroBanner
          swaps itself for a skeleton until its queries resolve, which used to
          leave the page with no h1 at all on first paint. */}
      <h1 className="sr-only">Dashboard</h1>

      {/* 01 · Standing */}
      <HeroBanner />

      {/* 02 · Volume, rolling window */}
      <RangeCounters />

      {/* 03 · Body composition */}
      <ScanStrip />

      {/* 04 · Against the viewer's own reference age */}
      <Section
        index="04"
        eyebrow="Strength pillar · unpacked"
        title={referenceTitle}
        subtitle="Your best logged load on each station, measured against the sarcopenia reference for your age. This is where the Strength pillar comes from: the mean of these six, anything over reference counted as 100."
      >
        {strengthFailed ? (
          <div className="mt-2">
            <SectionError subject="Your strength reference" sources={[...reference.sources, ...bodyMap.sources]} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-x-16 gap-y-10 items-start mt-2">
            <ReferenceTable />
            <BodyMapPanel />
          </div>
        )}
      </Section>

      {/* 05 · What you actually did */}
      <Section index="05" eyebrow="Logged" title="What you actually did">
        {loggedFailed ? (
          <div className="mt-2">
            <SectionError subject="Your training log" sources={[...digest.sources, ...volume.sources, ...attendance.sources]} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[6fr_4fr] gap-7 items-start mt-2">
            <AsyncPanel
              sources={digest.sources}
              hasContent={digest.hasContent}
              subject="Your last session"
              skeleton={
                <PanelSkeleton label="Loading last session">
                  <Shimmer height={320} />
                </PanelSkeleton>
              }
              // A user with no session yet has nothing to digest; the column
              // stays empty, as it always has.
              empty={<div />}
            >
              {() =>
                digest.data?.last_session && (
                  <SessionDigest
                    session={digest.data.last_session}
                    prs={digest.data.last_session_prs ?? []}
                    headline={digest.data.last_session_headline ?? null}
                  />
                )
              }
            </AsyncPanel>
            <Panel variant="dark" className="px-7 py-7 sm:px-7.5">
              <TonnageChart />
              <Hairline variant="soft" className="my-8" />
              <WeeklyAttendance />
            </Panel>
          </div>
        )}
      </Section>

      {/* 06–08 · The analysis */}
      <AnalysisSections aiOff={aiOff} />
    </div>
  )
}
